// src/hooks/useActivityTracking.ts
import { useCallback, useEffect, useRef } from 'react'
import { useTeamContext } from '@/contexts/TeamContext'

type EventType =
	| 'session_start' | 'session_end'
	| 'play_open' | 'play_close' | 'play_edit'
	| 'playbook_open' | 'playbook_close'
	| 'document_open' | 'document_close'
	| 'formation_create' | 'formation_edit'

type EntityType = 'play' | 'playbook' | 'document' | 'formation'

interface TrackEventOptions {
	entityType?: EntityType
	entityId?: string
	duration?: number
	metadata?: Record<string, unknown>
}

// Generate session ID once per browser session
const getSessionId = (): string => {
	let sessionId = sessionStorage.getItem('activity_session_id')
	if (!sessionId) {
		sessionId = crypto.randomUUID()
		sessionStorage.setItem('activity_session_id', sessionId)
	}
	return sessionId
}

export function useActivityTracking() {
	const { currentTeamId } = useTeamContext()

	const trackEvent = useCallback(
		async (eventType: EventType, options: TrackEventOptions = {}) => {
			try {
				await fetch('/api/activity', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					credentials: 'include',
					body: JSON.stringify({
						eventType,
						teamId: currentTeamId,
						sessionId: getSessionId(),
						entityType: options.entityType,
						entityId: options.entityId,
						duration: options.duration,
						metadata: options.metadata,
					}),
				})
			} catch {
				// Silently fail - activity tracking shouldn't break the app
			}
		},
		[currentTeamId],
	)

	return { trackEvent }
}

/**
 * Hook for tracking view duration of an entity.
 * Tracks open on mount, close with duration on unmount.
 */
export function useEntityViewTracking(
	entityType: EntityType,
	entityId: string | undefined,
) {
	const { trackEvent } = useActivityTracking()
	const openTimeRef = useRef<number | null>(null)

	useEffect(() => {
		if (!entityId) return

		openTimeRef.current = Date.now()
		trackEvent(`${entityType}_open` as EventType, { entityType, entityId })

		return () => {
			if (openTimeRef.current) {
				const duration = Math.floor((Date.now() - openTimeRef.current) / 1000)
				trackEvent(`${entityType}_close` as EventType, {
					entityType,
					entityId,
					duration,
				})
			}
		}
	}, [entityType, entityId, trackEvent])
}
